// src/utils/email.js
import emailjs from "@emailjs/browser";
import { generateLeaseModelBase64 } from "./leaseModel";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const CONFIRM_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONFIRM_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// optional init; EmailJS also works without explicit init when you pass PUBLIC_KEY to send()
emailjs.init(PUBLIC_KEY);

function formatTimestampForEmail(value) {
  if (!value) return "";

  let d = value;

  // Firestore Timestamp: has a .toDate() method
  if (typeof value === "object" && typeof value.toDate === "function") {
    d = value.toDate();
  } else if (!(value instanceof Date)) {
    // String, number, etc.
    d = new Date(value);
  }

  if (Number.isNaN(d.getTime())) return "";

  const pad = (n, size = 2) => String(n).padStart(size, "0");

  // Example: 2025-11-21 13:45:32.123 (local time)
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `.${pad(d.getMilliseconds(), 3)}`
  );
}

export async function sendSubmissionEmail(exhibit, user) {
  if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
    console.warn("[EmailJS] Missing config, skipping email send.");
    return;
  }

  const { contactEmail, contactLastName, contactFirstName } = user || {};

  const {
    id,
    companyName,
    signature,
    serviceProvider,
    commonName,
    locationAddress,
    createdAt,
    city,
    state,
    sqftLeased,
    status,
    pdfURL,
    imgURLs = [],
  } = exhibit || {};

  let leaseModelBase64 = "";
  try {
    leaseModelBase64 = await generateLeaseModelBase64(exhibit);
  } catch (e) {
    console.error("[EmailJS] Failed to generate lease model:", e);
  }

  // ---------------------------------------------------------------------------
  // 1) INTERNAL TEAM EMAIL (existing)
  // ---------------------------------------------------------------------------
  const internalParams = {
    companyName: companyName || "",
    contactEmail: contactEmail || "",
    signature: signature || "",
    exhibit_id: id || "",
    serviceProvider: serviceProvider || "",
    common_name: commonName || "",
    locationAddress: locationAddress || "",
    city: city || "",
    state: state || "",
    sqft_leased: sqftLeased || "",
    createdAt: formatTimestampForEmail(createdAt),
    status: status || "Submitted",

    pdf_attachment: pdfURL || "",
    lease_model: leaseModelBase64 || "",
    image_1_attachment: imgURLs[0] || "",
    image_2_attachment: imgURLs[1] || "",
    image_3_attachment: imgURLs[2] || "",
    image_4_attachment: imgURLs[3] || "",
    image_5_attachment: imgURLs[4] || "",
    image_6_attachment: imgURLs[5] || "",
    image_7_attachment: imgURLs[6] || "",
    image_8_attachment: imgURLs[7] || "",
    image_9_attachment: imgURLs[8] || "",
    image_10_attachment: imgURLs[9] || "",
    image_11_attachment: imgURLs[10] || "",
    image_12_attachment: imgURLs[11] || "",
    image_13_attachment: imgURLs[12] || "",
    image_14_attachment: imgURLs[13] || "",
    image_15_attachment: imgURLs[14] || "",
  };

  const internalEmailPromise = emailjs.send(
    SERVICE_ID,
    TEMPLATE_ID,
    internalParams,
    PUBLIC_KEY
  );

  // ---------------------------------------------------------------------------
  // 2) USER CONFIRMATION EMAIL (new)
  // ---------------------------------------------------------------------------
  if (!CONFIRM_TEMPLATE_ID) {
    console.warn("[EmailJS] No CONFIRM_TEMPLATE_ID provided");
  }

  const confirmParams = {
    contactEmail: contactEmail || "", // user receiving the confirmation
    serviceProvider: serviceProvider || "",
    locationAddress: locationAddress || "",
    city: city || "",
    state: state || "",
    companyName: companyName || "",
    pdf_attachment: pdfURL || "",
    image_1_attachment: imgURLs[0] || "",
    image_2_attachment: imgURLs[1] || "",
    image_3_attachment: imgURLs[2] || "",
    image_4_attachment: imgURLs[3] || "",
    image_5_attachment: imgURLs[4] || "",
    image_6_attachment: imgURLs[5] || "",
    image_7_attachment: imgURLs[6] || "",
    image_8_attachment: imgURLs[7] || "",
    image_9_attachment: imgURLs[8] || "",
    image_10_attachment: imgURLs[9] || "",
    image_11_attachment: imgURLs[10] || "",
    image_12_attachment: imgURLs[11] || "",
    image_13_attachment: imgURLs[12] || "",
    image_14_attachment: imgURLs[13] || "",
    image_15_attachment: imgURLs[14] || "",
  };

  const confirmEmailPromise = CONFIRM_TEMPLATE_ID
    ? emailjs.send(SERVICE_ID, CONFIRM_TEMPLATE_ID, confirmParams, PUBLIC_KEY)
    : Promise.resolve();

  // ---------------------------------------------------------------------------
  // Send both emails in parallel
  // ---------------------------------------------------------------------------
  return Promise.all([internalEmailPromise, confirmEmailPromise]);
}
